import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BarChart3, Download, Loader2 } from "lucide-react";

import { DatePicker } from "../../../components/ui/DatePicker";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { reportsService } from "../../../services/reports";
import { formatApiError } from "../../../services/api";
import { useAuth } from "../../../app/providers/AuthProvider";
import { Permission, hasPermission } from "../../../app/permissions";

const ORDER_STATUSES = [
  "order_placed",
  "confirmed",
  "in_progress",
  "shipped",
  "delivered",
  "collected",
  "quality_testing",
  "collection_complete",
  "cancelled",
  "rejected",
];

function fmtDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function StatCard({ label, value, testId }) {
  return (
    <div
      className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card"
      data-testid={testId}
    >
      <div className="text-[11px] uppercase tracking-wide text-text-secondary">
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-text-primary">{value}</div>
    </div>
  );
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // A14.2: warehouse reuses the reports UI but only sees the reports
  // it has access to. Products export stays admin-only; warehouse gets
  // orders + damages + two new operational CSVs.
  const isAdmin = hasPermission(user, Permission.ADMIN_USERS_MANAGE);

  const [from, setFrom] = useState(fmtDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [statusFilter, setStatusFilter] = useState("all");

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null); // "orders"|"products"|"damages"

  const rangeValid = Boolean(from && to && from <= to);
  const rangeError = useMemo(() => {
    if (!from || !to) return t("reports.err_range_required");
    if (from > to) return t("reports.err_range_order");
    // Rough client-side check for >1yr — the backend enforces authoritatively.
    const d1 = new Date(from);
    const d2 = new Date(to);
    const days = (d2 - d1) / (1000 * 60 * 60 * 24);
    if (days > 366) return t("reports.err_range_too_large");
    return null;
  }, [from, to, t]);

  const loadSummary = useCallback(async () => {
    if (rangeError) return;
    setLoading(true);
    setError(null);
    try {
      const s = await reportsService.summary({ from, to });
      setSummary(s);
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "INVALID_RANGE") setError(t("reports.err_range_order"));
      else if (code === "RANGE_TOO_LARGE") setError(t("reports.err_range_too_large"));
      else setError(formatApiError(err, t("common.error_generic")));
    } finally {
      setLoading(false);
    }
  }, [from, to, rangeError, t]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const download = async (kind) => {
    if (rangeError) return;
    setDownloading(kind);
    try {
      const params = { from, to };
      if (kind === "orders" && statusFilter !== "all") params.status = statusFilter;
      const { blob, filename } = await reportsService.downloadCsv(kind, params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("reports.download_success", { name: filename }));
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "INVALID_RANGE") toast.error(t("reports.err_range_order"));
      else if (code === "RANGE_TOO_LARGE") toast.error(t("reports.err_range_too_large"));
      else toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setDownloading(null);
    }
  };

  const statusEntries = summary
    ? Object.entries(summary.orders_by_status || {}).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-champagne" aria-hidden />
          <h1
            className="text-3xl font-semibold tracking-tight text-text-primary"
            data-testid="reports-title"
          >
            {t("reports.title")}
          </h1>
        </div>
        <p className="text-sm text-text-secondary">{t("reports.subtitle")}</p>
      </header>

      {/* Filter bar */}
      <div className="grid gap-4 rounded-lg border border-border-subtle bg-surface p-5 shadow-card sm:grid-cols-4">
        <div className="space-y-1.5">
          <Label>{t("reports.field_from")}</Label>
          <DatePicker
            value={from}
            onChange={(v) => setFrom(v || "")}
            required
            testId="reports-from"
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("reports.field_to")}</Label>
          <DatePicker
            value={to}
            onChange={(v) => setTo(v || "")}
            required
            testId="reports-to"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>{t("reports.field_orders_status")}</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="reports-status-filter"
              className="h-10 bg-surface border-border-subtle"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="reports-status-all">
                {t("reports.status_all")}
              </SelectItem>
              {ORDER_STATUSES.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`reports-status-${s}`}
                >
                  {t(`orders.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-text-secondary">{t("reports.status_hint")}</p>
        </div>
      </div>

      {rangeError && (
        <p
          className="text-sm text-status-unavailable"
          data-testid="reports-range-error"
        >
          {rangeError}
        </p>
      )}

      {/* Summary preview */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="reports-loading">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : error ? (
        <ErrorState
          title={t("common.error_generic")}
          body={error}
          onRetry={loadSummary}
          testId="reports-error"
        />
      ) : summary ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label={t("reports.stat_orders_total")}
              value={summary.orders_total}
              testId="reports-stat-orders-total"
            />
            <StatCard
              label={t("reports.stat_bottles_total")}
              value={summary.bottles_total}
              testId="reports-stat-bottles-total"
            />
            <StatCard
              label={t("reports.stat_active_users")}
              value={summary.active_users}
              testId="reports-stat-active-users"
            />
            <StatCard
              label={t("reports.stat_damages_total")}
              value={summary.damages_total}
              testId="reports-stat-damages-total"
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card">
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-sm font-semibold text-text-primary">
                  {t("reports.orders_by_status_heading")}
                </h2>
                <div className="h-px w-6 bg-champagne" aria-hidden />
              </div>
              {statusEntries.length === 0 ? (
                <EmptyState
                  title={t("reports.empty_status_title")}
                  body={t("reports.empty_status_body")}
                  testId="reports-status-empty"
                />
              ) : (
                <ul className="space-y-2" data-testid="reports-status-breakdown">
                  {statusEntries.map(([status, count]) => (
                    <li
                      key={status}
                      className="flex items-center justify-between text-sm"
                      data-testid={`reports-status-row-${status}`}
                    >
                      <StatusBadge status={status} />
                      <span className="font-mono text-text-primary">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card">
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-sm font-semibold text-text-primary">
                  {t("reports.top_products_heading")}
                </h2>
                <div className="h-px w-6 bg-champagne" aria-hidden />
              </div>
              {summary.top_products.length === 0 ? (
                <EmptyState
                  title={t("reports.empty_top_products_title")}
                  body={t("reports.empty_top_products_body")}
                  testId="reports-top-empty"
                />
              ) : (
                <ol
                  className="space-y-2 text-sm"
                  data-testid="reports-top-products"
                >
                  {summary.top_products.map((p, i) => (
                    <li
                      key={`${p.name}-${i}`}
                      className="flex items-center justify-between gap-3"
                      data-testid={`reports-top-row-${i}`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="w-5 shrink-0 text-right font-mono text-xs text-text-secondary">
                          {i + 1}.
                        </span>
                        <span className="truncate text-text-primary">{p.name}</span>
                      </span>
                      <span className="font-mono text-text-primary">{p.quantity}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </>
      ) : null}

      {/* Download row */}
      <div
        className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-surface p-5 shadow-card sm:flex-row sm:items-center sm:justify-between"
        data-testid="reports-download-row"
      >
        <div>
          <div className="text-sm font-semibold text-text-primary">
            {t("reports.download_heading")}
          </div>
          <p className="mt-0.5 text-xs text-text-secondary">
            {t("reports.download_hint")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { kind: "orders", label: t("reports.download_orders"), roles: ["admin", "warehouse"] },
            { kind: "products", label: t("reports.download_products"), roles: ["admin"] },
            { kind: "damages", label: t("reports.download_damages"), roles: ["admin", "warehouse"] },
            { kind: "warehouse_throughput", label: t("reports.download_warehouse_throughput"), roles: ["admin", "warehouse"] },
            { kind: "tracking_turnaround", label: t("reports.download_tracking_turnaround"), roles: ["admin", "warehouse"] },
          ]
            .filter(({ roles }) => roles.includes(user?.role))
            .map(({ kind, label }) => (
              <Button
                key={kind}
                type="button"
                onClick={() => download(kind)}
                disabled={Boolean(rangeError) || Boolean(downloading)}
                data-testid={`reports-download-${kind}`}
                variant="outline"
                className="border-border-subtle text-text-primary hover:bg-offwhite"
              >
                {downloading === kind ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {label}
              </Button>
            ))}
        </div>
      </div>
    </div>
  );
}
